import { ArrowUpRight, CalendarDays, Clock3, Mic2 } from "lucide-react";

import { PODCAST_CONFIG } from "../podcast-config";
import { getLatestPodcastEpisode } from "../podcast-rss";
import { usePodcastFeed } from "../usePodcastFeed";
import { PodcastEpisodeArchive } from "./PodcastEpisodeArchive";
import { PodcastNewsletterSignup } from "./PodcastNewsletterSignup";
import { PodcastPlatformIcon } from "./PodcastPlatformIcon";
import { PodcastYouTubeSection } from "./PodcastYouTubeSection";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("es-CO", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function PodcastSection() {
  const {
    name,
    tagline,
    secondaryTagline,
    description,
    audience,
    format,
    language,
    episodeDuration,
    author,
    coverImage,
    platforms,
  } = PODCAST_CONFIG;
  const podcastFeed = usePodcastFeed();
  const latestEpisode = getLatestPodcastEpisode(podcastFeed.episodes);

  return (
    <section id="podcast" className="w-full bg-white">
      <div className="mx-auto grid max-w-7xl md:grid-cols-[0.9fr_1.1fr]">
        <div className="flex items-center border-b border-black/10 bg-black p-6 sm:p-8 md:min-h-[500px] md:border-b-0 md:border-r">
          <div className="mx-auto w-full max-w-md">
            {coverImage ? (
              <img
                src={coverImage}
                alt={`Portada de ${name}`}
                className="aspect-square w-full border border-white/20 object-cover"
              />
            ) : (
              <div className="flex aspect-square items-center justify-center border border-white/20 text-white">
                <Mic2 className="h-16 w-16" />
              </div>
            )}
            <div className="mt-4 text-xs uppercase tracking-[0.13em] text-white/65">
              <div className="grid grid-cols-2">
                <span className="text-left">Por {author}</span>
                <span className="text-right">Nuevos episodios</span>
              </div>
              <div className="mt-3 flex items-center justify-center gap-2">
                {platforms.map((platform) =>
                  platform.url ? (
                    <a
                      key={platform.kind}
                      href={platform.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Escuchar en ${platform.name}`}
                      title={platform.name}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-white transition hover:scale-105 hover:border-white"
                    >
                      <PodcastPlatformIcon kind={platform.kind} size="large" />
                    </a>
                  ) : null,
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center px-4 py-5 sm:px-8 md:px-10 md:py-20 lg:px-16 lg:py-5">
          <div className="w-full max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-black/60">
              Pódcast
            </p>
            <h1 className="mt-4 text-4xl max-full font-semibold leading-[1.02] tracking-[-0.06em] sm:text-5xl lg:text-6xl">
              {name}
            </h1>
            <p className="mt-3 text-xl font-medium tracking-[-0.03em]">{tagline}</p>
            <p className="mt-3 max-full text-sm leading-6 text-black/55">{description}</p>
            <p className="mt-3 text-xs font-semibold text-black/70">{secondaryTagline}</p>

            <dl className="mt-5 grid gap-x-6 gap-y-3 border-y border-black/10 py-4 text-xs sm:grid-cols-[2fr_1fr_1fr] max-full">
              <div>
                <dt className="text-black/40">Público</dt>
                <dd className="mt-1 font-medium">{audience}</dd>
              </div>
              <div>
                <dt className="text-black/40">Duración</dt>
                <dd className="mt-1 font-medium">{episodeDuration}</dd>
              </div>

              <div>
                <dt className="text-black/40">Idioma</dt>
                <dd className="mt-1 font-medium">{language}</dd>
              </div>
            </dl>

            {latestEpisode?.url && (
              <a
                href={latestEpisode.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block border-b border-black/15 py-3"
              >
                <span className="flex items-center justify-between gap-4">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-black/60">
                    Último episodio
                  </span>
                  <span className="flex items-center gap-3 text-xs text-black/60">
                    <span className="flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" />
                      {formatDate(latestEpisode.publishedAt)}
                    </span>
                    {latestEpisode.duration && (
                      <span className="flex items-center gap-1">
                        <Clock3 className="h-3 w-3" />
                        {latestEpisode.duration}
                      </span>
                    )}
                  </span>
                </span>
                <span className="mt-3 flex items-center justify-between gap-5">
                  <span>
                    <span className="block text-base font-semibold">{latestEpisode.title}</span>
                    <span
                      className="mt-1 block overflow-hidden text-xs leading-5 text-black/50"
                      style={{
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                      }}
                    >
                      {latestEpisode.description}
                    </span>
                  </span>
                  <ArrowUpRight className="h-5 w-5 shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </a>
            )}
          </div>
        </div>
      </div>
      <PodcastEpisodeArchive archive={podcastFeed} />
      <PodcastYouTubeSection />
      <PodcastNewsletterSignup />
    </section>
  );
}
